import { useCallback, useMemo } from 'react';
import useQueryParams from 'hooks/useQueryParams';
import usePageParams from 'hooks/usePageParams';
import { FIRST_PAGE } from 'data/global';
import { WaitingRoomParams } from 'router/subrouters/DataRoom/dataRoomApi/waiting-room';
import utils, { getHasElementsInArray } from 'utils';
import { LAST_SEEN_FILTER } from 'router/subrouters/DataRoom/subrouters/Members/pages/Members/components/MembersFlatTable/data';
import {
  DEFAULT_WAITING_ROOM_SORT,
  TYPE_FILTER_VALUES,
  WAITING_ROOM_FILTER_PARAMS,
  WAITING_ROOM_LIMIT,
  WAITING_ROOM_PAGE_PARAM,
  WAITING_ROOM_SORT_PARAM,
} from '../components/WaitingRoomTable/data';

type WaitingRoomQueryParams = Record<string, string>;

// Single place the table's URL state is turned into a request. Filters slot in
// next to `types` — nothing else needs to know they exist.
export default function useWaitingRoomParams() {
  const { params, setMultipleQueryParams } =
    useQueryParams<WaitingRoomQueryParams>();

  const page = usePageParams(WAITING_ROOM_PAGE_PARAM);

  const sort = params[WAITING_ROOM_SORT_PARAM] || DEFAULT_WAITING_ROOM_SORT;

  const requestors = params[WAITING_ROOM_FILTER_PARAMS.REQUESTORS];
  const emails = params[WAITING_ROOM_FILTER_PARAMS.EMAILS];
  const requestingAccessTo =
    params[WAITING_ROOM_FILTER_PARAMS.REQUESTING_ACCESS_TO];

  // "All time" is the filter not being applied, so it is dropped rather than
  // sent — same as the members table, where the option has no predicate.
  const requestedAtParam = params[WAITING_ROOM_FILTER_PARAMS.REQUESTED];

  const requestedAt =
    requestedAtParam === LAST_SEEN_FILTER.ALL_TIME
      ? undefined
      : requestedAtParam;

  // The URL holds the two groups the type filter offers; the BE only knows the
  // four request types, so "Secure link" is expanded into its three here. Every
  // other filter is already a list of values the BE handed out, and rides along
  // as it stands.
  const typeGroups = params[WAITING_ROOM_FILTER_PARAMS.TYPES];

  const types = useMemo(() => {
    const groups = utils.mapParamsToArray(typeGroups);

    if (!getHasElementsInArray(groups)) return undefined;

    return utils.mapArrayToParams(
      groups.flatMap((group) => TYPE_FILTER_VALUES[group] ?? []),
    );
  }, [typeGroups]);

  const apiParams = useMemo<WaitingRoomParams>(
    () => ({
      $page: page,
      $limit: WAITING_ROOM_LIMIT,
      $sort: sort,
      ...(types && { types }),
      ...(requestors && { requestors }),
      ...(emails && { emails }),
      ...(requestingAccessTo && { requestingAccessTo }),
      ...(requestedAt && { requestedAt }),
    }),
    [page, sort, types, requestors, emails, requestingAccessTo, requestedAt],
  );

  // Re-sorting or re-filtering reshuffles the whole queue, so staying on page 4
  // would land the user on a page the new result may not even have.
  const updateSort = useCallback(
    (value: string) =>
      setMultipleQueryParams({
        [WAITING_ROOM_SORT_PARAM]: value,
        [WAITING_ROOM_PAGE_PARAM]: FIRST_PAGE,
      }),
    [setMultipleQueryParams],
  );

  return { apiParams, sort, updateSort };
}
